import moduleMasteryJson from "./generated/moduleMastery.json";
import { ALL_MODULE_DEFINITIONS } from "./moduleDefinitions.js";
import { parseModuleEffect, type ModuleEffectData } from "../modules/ModuleEffect.js";

/**
 * 모듈별 숙련도 노드 카탈로그 — `data/moduleMastery.tsv`가 SSoT (TSV → generated JSON → 본 파서).
 *
 * 모듈을 쓸수록 누적되는 XP가 노드별 임계(requiredXp)에 도달하면 노드가 **자동 해금**되어 발사체 효과를 강화한다.
 * 효과는 코드 하드코딩이 아니라 modules.tsv와
 * **동일한 mini-DSL**(effectDsl 컬럼)을 parseModuleEffect로 재사용한다 — 신규 효과 키 추가 없음.
 *
 * 레벨 3 A/B는 **분기 노드**(상호배타) — ModuleMasteryController.selectedBranch가 일치할 때만 적용된다.
 * displayName/description은 개발 참조용(UI 라벨은 i18n `mastery.*` 키가 SSoT).
 */

/** 레벨 3 분기 — 같은 레벨에서 A/B 중 selectedBranch와 일치하는 하나만 적용. */
export type ModuleMasteryBranch = "A" | "B";

/** 숙련도 노드 1개의 정의 (순수 데이터). effect는 effectDsl을 parseModuleEffect로 파싱한 결과. */
export interface ModuleMasteryNodeDefinition {
  readonly id: string;
  readonly moduleId: string;
  readonly level: number;
  /** 비분기 노드는 undefined. A/B는 selectedBranch 일치 시만 적용. */
  readonly branch?: ModuleMasteryBranch;
  /** 누적 숙련도 XP 요구량. */
  readonly requiredXp: number;
  /** 개발 참조용 이름 (UI는 i18n `mastery.*`). */
  readonly displayName: string;
  /** 개발 참조용 설명. */
  readonly description: string;
  /** modules.tsv와 동일 mini-DSL을 파싱한 효과 — DefinitionModule이 ALWAYS_PROC로 적용. */
  readonly effect: ModuleEffectData;
}

/** 한 모듈의 숙련도 노드 묶음. */
export interface ModuleMasteryDefinition {
  readonly moduleId: string;
  readonly nodes: readonly ModuleMasteryNodeDefinition[];
}

/** 활성(enable=true) 모듈 id — 숙련도 대상 모듈 게이트(비활성/오타 moduleId 제외). */
const ACTIVE_MODULE_IDS: ReadonlySet<string> = new Set(ALL_MODULE_DEFINITIONS.map((d) => d.id));

/** generated/moduleMastery.json 1행의 원시 형태 (commentC는 export에서 제외됨). */
export interface RawMasteryRow {
  readonly id: string;
  readonly moduleId: string;
  readonly level: number;
  readonly branch: string;
  readonly requiredXp: number;
  readonly displayName: string;
  readonly description: string;
  readonly effectDsl: string;
  readonly enable: boolean;
}

/**
 * 원시 행 → 검증된 노드 정의 (순수). 잘못된 행은 제외하고, 같은 id 중복은 throw한다.
 *  - enable=false / requiredXp<=0 / 비활성 moduleId → 제외
 *  - branch는 "", "A", "B"만 허용(그 외 제외)
 *  - 동일 id 중복 → throw (테스트가 캐치 — 데이터 오류 조기 차단)
 */
function buildNodes(rows: readonly RawMasteryRow[]): ModuleMasteryNodeDefinition[] {
  const seen = new Set<string>();
  const out: ModuleMasteryNodeDefinition[] = [];
  for (const r of rows) {
    if (!r.enable) continue;
    if (!ACTIVE_MODULE_IDS.has(r.moduleId)) continue;
    if (!(r.requiredXp > 0)) continue;
    if (r.branch !== "" && r.branch !== "A" && r.branch !== "B") continue;
    if (seen.has(r.id)) {
      throw new Error(`[moduleMastery] duplicate node id: ${r.id}`);
    }
    seen.add(r.id);
    out.push({
      id: r.id,
      moduleId: r.moduleId,
      level: r.level,
      ...(r.branch === "A" || r.branch === "B" ? { branch: r.branch } : {}),
      requiredXp: r.requiredXp,
      displayName: r.displayName,
      description: r.description,
      effect: parseModuleEffect(r.effectDsl),
    });
  }
  return out;
}

/** moduleId별 노드 묶음으로 그룹화 (선언 순서 보존). */
function groupByModule(nodes: readonly ModuleMasteryNodeDefinition[]): ModuleMasteryDefinition[] {
  const byModule = new Map<string, ModuleMasteryNodeDefinition[]>();
  for (const n of nodes) {
    const list = byModule.get(n.moduleId);
    if (list) list.push(n);
    else byModule.set(n.moduleId, [n]);
  }
  return [...byModule.entries()].map(([moduleId, list]) => ({ moduleId, nodes: list }));
}

/**
 * 원시 행 배열 → moduleId별 숙련도 정의 (순수, 검증 포함). 테스트 주입용으로 export.
 * 실데이터 파싱(ALL_MODULE_MASTERY_DEFINITIONS)과 동일 경로를 공유한다.
 */
export function parseMasteryDefinitions(
  rows: readonly RawMasteryRow[],
): ModuleMasteryDefinition[] {
  return groupByModule(buildNodes(rows));
}

const BY_MODULE = new Map<string, ModuleMasteryDefinition>(
  parseMasteryDefinitions(moduleMasteryJson as readonly RawMasteryRow[]).map((d) => [d.moduleId, d]),
);

/** 전체 모듈 숙련도 정의 (moduleId별 묶음). */
export const ALL_MODULE_MASTERY_DEFINITIONS: readonly ModuleMasteryDefinition[] = [
  ...BY_MODULE.values(),
];

/** moduleId의 숙련도 정의 (없으면 undefined). */
export function masteryDefinitionOf(moduleId: string): ModuleMasteryDefinition | undefined {
  return BY_MODULE.get(moduleId);
}

/** moduleId의 숙련도 노드 목록 (없으면 빈 배열). */
export function masteryNodesOf(moduleId: string): readonly ModuleMasteryNodeDefinition[] {
  return BY_MODULE.get(moduleId)?.nodes ?? [];
}
